import type { FunctionComponent } from 'react';
import { useState } from 'react';

import { INTEGRATION_NAME_MAP } from 'constants/reporting';
import { logException } from 'helpers/loggers';
import { publishMenus } from 'helpers/menuManagement';
import {
  getPublishErrorType,
  trackMenuPublished,
  trackMenuPublishFailed,
} from 'helpers/menuAnalytics';

import {
  Accordion,
  Avatar,
  Button,
  Divider,
  Group,
  LoadingOverlay,
  Stack,
  Text,
} from '@mantine/core';

import NotificationWrapper from 'primitives/NotificationWrapper/NotificationWrapper';
import SourceAvatar from 'components/SourceAvatar';
import { WarningIconMd } from '@chownow/pantry/icons';
import type {
  BadRequestError,
  Menu,
  PublishBuildData,
  PublishBuildIntegration,
  PublishResult,
} from '../types';

type PublishStepProps = {
  gap: string;
  menu: Menu;
  nextStep: () => void;
  handleDoneClick: () => void;
  handlePublishFailed: (errorMessage?: string) => void;
  setPublishResult: (result: PublishResult | null) => void;
  publishData: PublishBuildData | null;
  publishBuildHasError: boolean;
  publishBuildErrorMessage?: string | null;
  isFetchingPublishData: boolean;
  /** Analytics `source`. Omitted by the menu editor, which is the default surface. */
  source?: string;
};

function menuCount(integration: PublishBuildIntegration) {
  return integration.merchants.reduce((previous, merchant) => previous + merchant.menus.length, 0);
}

function renderIntegrationPublishData(integrations: PublishBuildIntegration[]) {
  return integrations.map(integration => (
    <Accordion variant="bordered-card" key={integration.integration_name}>
      <Accordion.Item value={integration.integration_name}>
        <Accordion.Control>
          <Group gap="xs2" wrap="nowrap">
            <Avatar variant="outline" size="md">
              <SourceAvatar source={integration.integration_name} />
            </Avatar>
            <Text variant="title-sm">
              {(INTEGRATION_NAME_MAP as Record<string, string>)[integration.integration_name]}:
              Merchants ({integration.merchants.length}), Menus ({menuCount(integration)})
            </Text>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          {integration.merchants.map((merchant, index) => (
            <Stack key={merchant.merchant_id} gap="xs" mt={index === 0 ? 0 : 'md'}>
              <Stack gap="0">
                <Text variant="title-sm" mt={index === 0 ? 0 : 'md'}>
                  {merchant.merchant_name} ({merchant.merchant_id})
                </Text>
                <Divider variant="dashed" my="md" />
                <Stack gap="0">
                  {merchant.menus.map((m, menuIndex) => (
                    <Stack gap="md" key={m.menu_uuid}>
                      {menuIndex !== 0 && <Divider variant="dashed" mt="md" />}
                      <Stack gap="xs2">
                        <Text variant="label-sm">{m.menu_name}</Text>
                        <Text variant="sublabel-xs" c="var(--content-secondary)">
                          {m.menu_uuid}
                        </Text>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Stack>
          ))}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  ));
}

function renderInactiveIntegrationsPublishData(inactiveIntegrations: string[]) {
  if (!inactiveIntegrations.length) return null;
  return (
    <Accordion variant="bordered-card">
      <Accordion.Item value="inactiveIntegrations">
        <Accordion.Control>
          <Group gap="xs2">
            <WarningIconMd />
            <Text variant="title-sm">Inactive Integrations</Text>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          {inactiveIntegrations.map((message, index) => (
            <Stack key={message} gap={0}>
              {index !== 0 && <Divider variant="dashed" my="md" />}
              <Text>{message}</Text>
            </Stack>
          ))}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

export const PublishStep: FunctionComponent<PublishStepProps> = ({
  gap,
  menu,
  nextStep,
  handleDoneClick,
  handlePublishFailed,
  setPublishResult,
  publishData,
  publishBuildHasError,
  publishBuildErrorMessage,
  isFetchingPublishData,
  source,
}) => {
  const [isPublishing, setIsPublishing] = useState(false);

  const integrations = publishData?.integrations ?? [];
  const inactiveIntegrations = publishData?.integration_inactive_messages ?? [];

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      const result = await publishMenus({ menu, data: publishData });
      setPublishResult(result);
      trackMenuPublished({
        menuId: menu?.uuid?.toString(),
        responses: result?.responses || [],
        source,
      });
      nextStep();
    } catch (error) {
      const response = (error as BadRequestError)?.response;
      const detail = response?.data?.detail;
      const isBadRequest = response?.status === 400 && typeof detail === 'string';
      trackMenuPublishFailed({
        menuId: menu?.uuid?.toString(),
        errorType: getPublishErrorType(error),
        source,
      });
      handlePublishFailed(isBadRequest ? detail : undefined);
      logException({ message: 'Publish menus failed', error, context: {}, tags: {} });
    } finally {
      setIsPublishing(false);
    }
  };

  if (publishBuildHasError) {
    return (
      <Stack gap={gap}>
        <NotificationWrapper
          size="compact"
          variant="caution"
          styles={{
            description: {
              height: 'auto',
              whiteSpace: 'normal',
              overflow: 'visible',
              textOverflow: 'unset',
              lineHeight: 'var(--line-height-normal)',
              flexWrap: 'wrap',
            },
          }}
        >
          {publishBuildErrorMessage}
        </NotificationWrapper>
        <Button onClick={handleDoneClick} fullWidth>
          Done
        </Button>
      </Stack>
    );
  }

  const showLoader = isFetchingPublishData && !publishData;

  return (
    <Stack gap={gap}>
      <LoadingOverlay visible={showLoader} />
      {renderIntegrationPublishData(integrations)}
      {renderInactiveIntegrationsPublishData(inactiveIntegrations)}
      <Button
        loading={isPublishing}
        disabled={!publishData || !integrations.length}
        onClick={() => {
          void handlePublish();
        }}
        fullWidth
      >
        Publish
      </Button>
    </Stack>
  );
};
